import { useEffect, useState } from 'react'

import { faStar } from '@fortawesome/pro-regular-svg-icons'
import { faCalendar, faComment, faExternalLink } from '@fortawesome/pro-solid-svg-icons'
import { faStar as faSolidStar } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'

import ContactHighSchoolCounselorCallToAction from 'components/ContactHighSchoolCounselorCallToAction'
import { useAppSelector } from 'store'

import { ToggleTargeted } from '../ExploreTable/useTargetHighschoolsExplore'
import { HighschoolData } from './useGetHighschoolPreviewData'

const useStyles = makeStyles(() => ({
  buttonGroup: {
    '& .MuiButton-iconSizeSmall > *:first-child': {
      fontSize: 14,
    },
  },
}))

interface PreviewHeader {
  highschool: HighschoolData
  setInnerModalOpen: (v: boolean) => void
  toggleTargeted: ToggleTargeted
}
const PreviewHeader = ({ highschool, setInnerModalOpen, toggleTargeted }: PreviewHeader) => {
  const isPremiumExplorerEnabled = useAppSelector(
    (state) => state.college.record?.Features?.SupportsPremiumHighSchoolExplorer || false,
  )
  const [isTargeted, setIsTargeted] = useState(highschool.target ?? false)

  useEffect(() => {
    setIsTargeted(highschool.target ?? false)
  }, [highschool.target])

  const handleClick = () => {
    const next = !isTargeted
    setIsTargeted(next)
    toggleTargeted([{ hsId: highschool.id, targeted: next }]).then((ok) => {
      if (!ok) setIsTargeted(!next)
    })
  }

  const classes = useStyles()
  const viewLink = `selectedSchools=${encodeURIComponent(
    JSON.stringify([
      {
        id: highschool.id,
        name: highschool.name,
      },
    ]),
  )}`

  const date = highschool?.nextVisitDate ? format(new Date(highschool.nextVisitDate), 'yyyyMMdd') : ''

  // Some highschools returning urls that don't have http://, this should prevent that from breaking.
  const ensureHttpPrefix = (url: string) => {
    if (!url) return ''
    return url.startsWith('http://') || url.startsWith('https://') ? url : `http://${url}`
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gridGap={16}
      mx={3}
      my={2}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Typography variant="h5">{highschool.name}</Typography>
          <Typography
            variant="body2"
            color="textSecondary"
          >
            {highschool.address} {highschool.city !== '' ? `${highschool.city},` : ''}{' '}
            {highschool.state !== '' ? `${highschool.state}` : ''} {highschool.zip ? `${highschool.zip}` : ''}
          </Typography>
        </Box>
        {isPremiumExplorerEnabled && (
          <Button
            color="primary"
            variant={isTargeted ? 'outlined' : 'contained'}
            startIcon={<FontAwesomeIcon icon={isTargeted ? faSolidStar : faStar} />}
            onClick={handleClick}
          >
            {isTargeted ? 'Targeted' : 'Mark as Target'}
          </Button>
        )}
      </Box>
      <Box
        display="flex"
        gridGap={8}
        alignItems="center"
        className={classes.buttonGroup}
      >
        {highschool.isActive && (
          <>
            <Button
              component={Link}
              disabled={!highschool?.nextVisitDate}
              size="small"
              variant="outlined"
              color="primary"
              to={`${import.meta.env.BASE_URL}/recruit/visits/view/week?focus=${date}&${viewLink}`}
              endIcon={<FontAwesomeIcon icon={faCalendar} />}
            >
              View Availability
            </Button>
            <ContactHighSchoolCounselorCallToAction
              onOpenChange={setInnerModalOpen}
              variant="outlined"
              color="primary"
              size="small"
              highSchoolId={highschool.id}
              highSchoolName={highschool.name}
              contactUserId={highschool.contactUserId}
              endIcon={<FontAwesomeIcon icon={faComment} />}
            >
              Message
            </ContactHighSchoolCounselorCallToAction>
          </>
        )}
        {highschool.website && (
          <Button
            component="a"
            target="_blank"
            rel="noopener"
            href={ensureHttpPrefix(highschool.website)}
            size="small"
            variant="outlined"
            color="primary"
            endIcon={<FontAwesomeIcon icon={faExternalLink} />}
          >
            Website
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default PreviewHeader
